import { z } from "zod/v4";
import { courtIdSchema, reservationIdSchema, venueIdSchema } from "./ids";
import { currencySchema } from "./venue";

export const reservationStatusSchema = z.enum([
	"held",
	"confirmed",
	"cancelled",
	"expired",
	"noShow",
]);
export const settlementSchema = z.enum([
	"authorized",
	"online",
	"onSite",
	"unpaid",
	"complimentary",
]);
export const blockReasonSchema = z.enum(["maintenance", "training", "event", "closure", "other"]);
export const cancelReasonSchema = z.enum(["weather", "maintenance", "other"]);
export const cancelledBySchema = z.enum(["customer", "venue", "system"]);
export const manualSettlementSchema = z.enum(["onSite", "unpaid", "complimentary"]);

const reservationPolicySchema = z.object({
	freeHours: z.number().int().optional(),
	kind: z.enum(["always_free", "free_until", "non_refundable"]),
	lateRefundBps: z.number().int(),
});

const reservationBase = z.object({
	cancelledAt: z.iso.datetime({ offset: true }).optional(),
	cancelledBy: cancelledBySchema.optional(),
	cancelReason: cancelReasonSchema.optional(),
	courtId: courtIdSchema,
	endsAt: z.iso.datetime({ offset: true }),
	holdExpiresAt: z.iso.datetime({ offset: true }).optional(),
	id: reservationIdSchema,
	noShowAt: z.iso.datetime({ offset: true }).optional(),
	policy: reservationPolicySchema,
	refundRequestedCents: z.number().int().optional(),
	/** Set when the booking is one week of a recurrence. */
	seriesId: z.uuid().optional(),
	settlement: settlementSchema.optional(),
	startsAt: z.iso.datetime({ offset: true }),
	status: reservationStatusSchema,
	venueId: venueIdSchema,
});

const priced = {
	currency: currencySchema,
	priceCents: z.number().int().nonnegative(),
	vatRateBps: z.number().int(),
};

export const manualCustomerSchema = z.object({
	email: z.string().optional(),
	name: z.string(),
	phone: z.string().optional(),
});

const onlineReservation = reservationBase.extend({
	...priced,
	/** The player's display name, when the venue desk's read has one (never on the platform console's). */
	customerName: z.string().optional(),
	purpose: z.literal("online"),
});
const blockReservation = reservationBase.extend({
	blockReason: blockReasonSchema,
	purpose: z.literal("block"),
});

// Discriminated on `purpose`: a block carries a reason and no price; a manual
// booking carries its customer; an online booking carries neither.
export const reservationSchema = z.discriminatedUnion("purpose", [
	onlineReservation,
	reservationBase.extend({
		...priced,
		manualCustomer: manualCustomerSchema,
		purpose: z.literal("manual"),
	}),
	blockReservation,
]);

/** What the platform console receives: a manual booking without its walk-in customer's contact. */
export const adminReservationSchema = z.discriminatedUnion("purpose", [
	onlineReservation,
	reservationBase.extend({ ...priced, purpose: z.literal("manual") }),
	blockReservation,
]);

export const staffReservationRequestSchema = z.discriminatedUnion("purpose", [
	z.object({
		courtId: courtIdSchema,
		customer: z.object({
			email: z.email().optional(),
			name: z.string().trim().min(1).max(120),
			phone: z.string().trim().min(1).optional(),
		}),
		minutes: z.number().int().min(1).max(10_080),
		purpose: z.literal("manual"),
		settlement: manualSettlementSchema,
		startsAt: z.iso.datetime({ offset: true }),
	}),
	z.object({
		courtId: courtIdSchema,
		minutes: z.number().int().min(1).max(10_080),
		purpose: z.literal("block"),
		reason: blockReasonSchema,
		startsAt: z.iso.datetime({ offset: true }),
	}),
]);

/** Why a week of a recurrence was not booked (venue `recurrences_api`). */
export const skipReasonSchema = z.enum(["slot_taken", "not_bookable"]);

/** What a recurrence achieved: every booked week, and every week that could not be had. */
export const recurrenceResultSchema = z.object({
	created: z.array(reservationSchema),
	skipped: z.array(
		z.object({ reason: skipReasonSchema, startsAt: z.iso.datetime({ offset: true }) }),
	),
});

export const bulkCancellationSchema = z.object({
	courtIds: z.array(courtIdSchema).optional(),
	from: z.iso.datetime({ offset: true }),
	reason: cancelReasonSchema,
	to: z.iso.datetime({ offset: true }),
});
export const bulkCancellationResultSchema = z.object({ cancelled: z.number().int().nonnegative() });

export type ReservationStatus = z.infer<typeof reservationStatusSchema>;
export type Settlement = z.infer<typeof settlementSchema>;
export type BlockReason = z.infer<typeof blockReasonSchema>;
export type CancelReason = z.infer<typeof cancelReasonSchema>;
export type ManualSettlement = z.infer<typeof manualSettlementSchema>;
export type Reservation = z.infer<typeof reservationSchema>;
export type AdminReservation = z.infer<typeof adminReservationSchema>;
export type StaffReservationRequest = z.infer<typeof staffReservationRequestSchema>;
/** A desk booking repeated weekly: `weeks` counts the first occurrence. */
export type StaffRecurrenceRequest = StaffReservationRequest & { weeks: number };
export type RecurrenceResult = z.infer<typeof recurrenceResultSchema>;
export type SkipReason = z.infer<typeof skipReasonSchema>;
export type BulkCancellation = z.infer<typeof bulkCancellationSchema>;
